import React from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

export function LegalPage({ title, subtitle, children, testId }) {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <section className="max-w-3xl mx-auto px-5 lg:px-8 py-14" data-testid={testId}>
        <div className="overline mb-3">LEGAL</div>
        <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">{title}</h1>
        {subtitle && <p className="text-neutral-500 mt-3 text-sm">{subtitle}</p>}
        <div className="prose-doc mt-10 text-neutral-800">
          {children}
        </div>
      </section>
      <Footer />
    </div>
  );
}

const EFFECTIVE = "February 2026";

export function Terms() {
  return (
    <LegalPage title="Terms of Service" subtitle={`Effective ${EFFECTIVE}`} testId="terms-page">
      <h2>1. Agreement</h2>
      <p>By creating an account or using GrantFlow ("Service", "we", "us"), you agree to these Terms of Service. If you do not agree, do not use the Service.</p>

      <h2>2. Who may use the Service</h2>
      <p>You must be at least 18 years old and legally able to enter into a binding contract on behalf of yourself or the organization you represent. The Service is intended for US-based nonprofits, LLCs, corporations, and sole proprietors seeking business grants.</p>

      <h2>3. Accounts</h2>
      <p>You are responsible for maintaining the confidentiality of your account credentials and for all activity under your account. Notify us immediately at <a href="mailto:support@grantflow.app">support@grantflow.app</a> of any unauthorized use.</p>

      <h2>4. Subscription and billing</h2>
      <ul>
        <li>Paid plans are billed via Stripe on a recurring monthly or annual basis.</li>
        <li>Subscriptions auto-renew until canceled.</li>
        <li>You may cancel anytime from your billing portal or by contacting support. Cancellation stops the next renewal; you retain access through the end of the paid period.</li>
        <li>Refunds are governed by our <a href="/refund">Refund Policy</a>.</li>
      </ul>

      <h2>5. AI-generated content</h2>
      <p>GrantFlow uses AI (Claude Sonnet 5) to draft documents grounded in the profile information you provide. AI output is a starting draft, not a substitute for professional legal, financial, or grant-writing advice. You are responsible for reviewing and verifying all content before submitting to any funder or government agency. We do not guarantee grant awards.</p>

      <h2>6. Acceptable use</h2>
      <p>You agree not to: (a) misrepresent your identity or organization; (b) submit fraudulent grant applications; (c) scrape, resell, or redistribute the grant database; (d) reverse-engineer the Service; (e) use the Service for any unlawful purpose.</p>

      <h2>7. Intellectual property</h2>
      <p>All content generated by the AI for you (documents, drafts, exports) is owned by you. The Service, its interface, grant database, and code remain the property of GrantFlow, Inc.</p>

      <h2>8. Disclaimers</h2>
      <p>The Service is provided "as is" without warranties of any kind. We do not warrant that grant listings are error-free, exhaustive, or current — always confirm details on the funder's official website before applying.</p>

      <h2>9. Limitation of liability</h2>
      <p>To the maximum extent permitted by law, our aggregate liability arising from your use of the Service is limited to the amount you paid in the 12 months preceding the claim.</p>

      <h2>10. Changes to these Terms</h2>
      <p>We may update these Terms from time to time. Material changes will be announced by email. Continued use after the effective date constitutes acceptance.</p>

      <h2>11. Governing law</h2>
      <p>These Terms are governed by the laws of the State of California, USA, without regard to conflict-of-law principles.</p>

      <h2>12. Contact</h2>
      <p>Questions? Email <a href="mailto:support@grantflow.app">support@grantflow.app</a>.</p>
    </LegalPage>
  );
}

export function Privacy() {
  return (
    <LegalPage title="Privacy Policy" subtitle={`Effective ${EFFECTIVE}`} testId="privacy-page">
      <h2>1. What we collect</h2>
      <ul>
        <li><strong>Account data:</strong> name, email, password (hashed with bcrypt), organization name and type.</li>
        <li><strong>Organization profile:</strong> state, industry, mission, team size, EIN (optional), website (optional). Used to power AI drafts and match scoring.</li>
        <li><strong>Application &amp; document data:</strong> the drafts and metadata you create in GrantFlow.</li>
        <li><strong>Payment data:</strong> handled by Stripe — we never store card numbers.</li>
        <li><strong>Technical data:</strong> IP address, browser, and usage logs for security and product improvement.</li>
      </ul>

      <h2>2. How we use it</h2>
      <ul>
        <li>To deliver the Service (grant matching, AI drafts, deadline reminders).</li>
        <li>To process payments and prevent fraud.</li>
        <li>To respond to support requests.</li>
        <li>To improve product features (aggregated, non-identifying analytics only).</li>
      </ul>

      <h2>3. AI &amp; third parties</h2>
      <p>AI drafts are generated by Anthropic's Claude Sonnet 5 via a paid API. Anthropic does not train on API traffic. We also use Stripe (payments) and MongoDB Atlas (hosting) — each with their own security certifications (SOC 2, PCI DSS).</p>

      <h2>4. Data sharing</h2>
      <p>We do <strong>not</strong> sell your data. We share data only with the processors named above (Anthropic, Stripe, MongoDB Atlas), and only when legally required.</p>

      <h2>5. Data retention</h2>
      <p>Account and document data are retained while your account is active. On account deletion, we remove personal data within 30 days (backups purged within 90 days).</p>

      <h2>6. Your rights</h2>
      <p>You may request access, correction, or deletion of your data at any time by emailing <a href="mailto:support@grantflow.app">support@grantflow.app</a>. We honor GDPR, CCPA, and state privacy law requests within 30 days.</p>

      <h2>7. Security</h2>
      <p>TLS 1.3 in transit. Passwords hashed with bcrypt. Rate-limited authentication endpoints. Security headers (HSTS, X-Frame-Options, X-Content-Type-Options, Permissions-Policy). Encrypted MongoDB storage. Least-privilege access controls.</p>

      <h2>8. Children</h2>
      <p>The Service is not directed at children under 13 and we do not knowingly collect data from them.</p>

      <h2>9. Contact</h2>
      <p>Privacy questions or requests: <a href="mailto:support@grantflow.app">support@grantflow.app</a>.</p>
    </LegalPage>
  );
}

export function Refund() {
  return (
    <LegalPage title="Refund &amp; Cancellation Policy" subtitle={`Effective ${EFFECTIVE}`} testId="refund-page">
      <h2>1. 14-day money-back guarantee</h2>
      <p>If you're not happy with GrantFlow within your first 14 days of a paid subscription, email <a href="mailto:support@grantflow.app">support@grantflow.app</a> and we'll refund the full amount, no questions asked.</p>

      <h2>2. Prorated refunds after 14 days</h2>
      <p>After the 14-day window, monthly plans are non-refundable for the current billing cycle. Annual plans may be canceled with a prorated refund of unused full months, at our discretion.</p>

      <h2>3. How to cancel</h2>
      <ul>
        <li><strong>Self-service:</strong> your Stripe billing portal link is on your Dashboard.</li>
        <li><strong>Email:</strong> <a href="mailto:support@grantflow.app">support@grantflow.app</a> — we cancel within 24 hours.</li>
      </ul>
      <p>Canceling stops the next renewal; you retain access to Pro features until the end of the current billing period.</p>

      <h2>4. Non-refundable circumstances</h2>
      <ul>
        <li>Failure to win a grant (no grant platform can guarantee awards).</li>
        <li>Failure to submit an application on time due to user delay.</li>
        <li>Third-party fees paid to funders or filing services.</li>
      </ul>

      <h2>5. Chargebacks</h2>
      <p>Please contact us before filing a chargeback — 95% of billing questions can be resolved by email within a business day. Chargebacks filed without contacting us may result in account suspension.</p>

      <h2>6. Contact</h2>
      <p>All refund questions: <a href="mailto:support@grantflow.app">support@grantflow.app</a>.</p>
    </LegalPage>
  );
}
